/**
 * @module surveys
 */
import { createReducer } from '@reduxjs/toolkit';
import { taskActions } from '~/behaviors/actions/task.actions';
import { taskTemplateActions } from '~/behaviors/actions/task_template.actions';
import { surveyElementActions } from '~/behaviors/actions/survey_element.action';
import { surveyActions } from '~/behaviors/actions/survey.actions';
import { metaActions } from '~/behaviors/actions/meta.actions';
import { accountActions } from '~/behaviors/actions/account.actions';
import { Survey } from '~/interfaces/survey.interface';

const defaultState = {};

const reset = () => defaultState;

/**
 * Iterate over each element of `action.payload.surveys` and merge each with any
 * existing entity with the same ID in the state (or create new one if none exist).
 */
const mergeSurveys = (state, action) => {
  const surveys: { [id: number]: Survey } = action?.payload?.entities?.surveys || {};

  Object.values(surveys).forEach((survey) => {
    state[survey.id] = {
      ...state[survey.id],
      ...survey,
    };
  });
};

const mergeSurveysWithSurveyElements = (state, action) => {
  const surveys: { [id: number]: Survey } = action?.payload?.entities.surveys || {};
  Object.values(surveys).forEach((survey) => {
    state[survey.id] = {
      ...state[survey.id],
      ...survey,
    };
  });
};

const mergeSurveyElementsForSurvey = (state, action) => {
  const { surveyId, surveyElementId } = action.payload;
  const currentSurveyElementIds = state[surveyId]?.surveyElements ?? [];
  const surveyElementIds = [...currentSurveyElementIds, surveyElementId];
  state[surveyId] = {
    ...state[surveyId],
    surveyElements: surveyElementIds,
  };
};

const removeSurveyElementFromSurvey = (state, action) => {
  const { surveyElementId, surveyId } = action.payload;
  const surveyElementIds = state[surveyId].surveyElements.filter((id) => id !== surveyElementId);
  state[surveyId] = {
    ...state[surveyId],
    surveyElements: surveyElementIds,
  };
};

const removeSurvey = (state, action) => {
  const surveyId = action?.payload?.surveyId;
  if (!surveyId || !state[surveyId]) return state;
  delete state[surveyId];
};

const mergeMigratedSurvey = (state, action) => {
  const surveyInfo = action?.payload?.migratedSurvey;
  state[surveyInfo.id] = {
    ...state[surveyInfo.id],
    surveyStatusId: surveyInfo.surveyStatusId,
    timezoneId: surveyInfo.timezoneId,
    startTimeUTC: surveyInfo.startTimeUTC,
    endTimeUTC: surveyInfo.endTimeUTC,
  };
};

// Stores the Survey Style for a survey that is being created. Using the ID 0 since this will never exists.
const setTemporalSurveyStyle = (state, action) => {
  const isContinuous = action?.payload?.isContinuous;
  state[0] = {
    isContinuous,
  };
};

const surveys = createReducer(defaultState, (builder) => {
  builder
    .addCase(metaActions.reset, reset)
    .addCase(accountActions.logout.pending, reset)
    .addCase(accountActions.switchCompany.pending, reset)
    .addCase(taskActions.fetchTaskList.fulfilled, mergeSurveys)
    .addCase(taskActions.fetchTask.fulfilled, mergeSurveys)
    .addCase(taskTemplateActions.fetchTaskTemplateList.fulfilled, mergeSurveys)
    .addCase(taskTemplateActions.fetchTaskTemplate.fulfilled, mergeSurveys)
    .addCase(surveyActions.fetchBasicSurveyList.fulfilled, mergeSurveys)
    .addCase(surveyActions.fetchSurveyListWithTimezone.fulfilled, mergeSurveys)
    .addCase(surveyActions.fetchSurveyWithSurveyElements.fulfilled, mergeSurveys)
    .addCase(surveyActions.createSurvey.fulfilled, mergeSurveys)
    .addCase(surveyActions.updateSurvey.fulfilled, mergeSurveys)
    .addCase(surveyActions.updateRecipientList.fulfilled, mergeSurveys)
    .addCase(surveyActions.cancelScheduleSurvey.fulfilled, mergeSurveys)
    .addCase(surveyElementActions.fetchSurveyElementsBySurveys.fulfilled, mergeSurveysWithSurveyElements)
    .addCase(surveyElementActions.createSurveyElement.fulfilled, mergeSurveyElementsForSurvey)
    .addCase(surveyElementActions.deleteSurveyElement.fulfilled, removeSurveyElementFromSurvey)
    .addCase(surveyActions.deleteSurvey.fulfilled, removeSurvey)
    .addCase(surveyActions.migrateSurvey.fulfilled, mergeMigratedSurvey)
    .addCase(surveyActions.approveMigratedSurvey.fulfilled, mergeMigratedSurvey)
    .addCase(surveyActions.setTemporalSurveyStyle, setTemporalSurveyStyle);
});

export default surveys;
